import { useLayoutEffect } from 'react';

import {
  buildAbsoluteUrl,
  getSiteOrigin,
  SEO_DEFAULT_DESCRIPTION,
  SEO_DEFAULT_IMAGE,
  SEO_INDEX_ROBOTS,
  SEO_NOINDEX_ROBOTS,
  SEO_SITE_NAME,
  serializeJsonLd,
  type JsonLdValue,
} from '@/lib/seo';

type SeoOptions = {
  title?: string;
  description?: string;
  canonicalPath?: string;
  image?: string;
  imageAlt?: string;
  indexable?: boolean;
  ogType?: 'website' | 'article';
  ogTitle?: string;
  ogDescription?: string;
  structuredData?: JsonLdValue;
};

function ensureManagedTitle(): HTMLTitleElement {
  let titleElement = document.head.querySelector<HTMLTitleElement>('title[data-seo="title"]');

  if (!titleElement) {
    titleElement = document.createElement('title');
    titleElement.setAttribute('data-seo', 'title');
    document.head.appendChild(titleElement);
  }

  return titleElement;
}

function ensureManagedMeta(key: string, attributeName: 'name' | 'property'): HTMLMetaElement {
  let metaElement = document.head.querySelector<HTMLMetaElement>(`meta[data-seo="${key}"]`);

  if (!metaElement) {
    metaElement = document.createElement('meta');
    metaElement.setAttribute('data-seo', key);
    metaElement.setAttribute(attributeName, key);
    document.head.appendChild(metaElement);
  }

  return metaElement;
}

function ensureManagedLink(key: string, rel: string): HTMLLinkElement {
  let linkElement = document.head.querySelector<HTMLLinkElement>(`link[data-seo="${key}"]`);

  if (!linkElement) {
    linkElement = document.createElement('link');
    linkElement.setAttribute('data-seo', key);
    linkElement.setAttribute('rel', rel);
    document.head.appendChild(linkElement);
  }

  return linkElement;
}

function ensureManagedScript(key: string): HTMLScriptElement {
  let scriptElement = document.head.querySelector<HTMLScriptElement>(`script[data-seo="${key}"]`);

  if (!scriptElement) {
    scriptElement = document.createElement('script');
    scriptElement.setAttribute('data-seo', key);
    scriptElement.setAttribute('type', 'application/ld+json');
    document.head.appendChild(scriptElement);
  }

  return scriptElement;
}

function updateMetaContent(key: string, attributeName: 'name' | 'property', content: string): void {
  const metaElement = ensureManagedMeta(key, attributeName);
  metaElement.setAttribute(attributeName, key);
  metaElement.setAttribute('content', content);
}

export function useSeo({
  title,
  description,
  canonicalPath,
  image,
  imageAlt,
  indexable = true,
  ogType = 'website',
  ogTitle,
  ogDescription,
  structuredData,
}: SeoOptions = {}): void {
  useLayoutEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const origin = getSiteOrigin();
    const resolvedTitle = title?.trim() || SEO_SITE_NAME;
    const resolvedDescription = description?.trim() || SEO_DEFAULT_DESCRIPTION;
    const resolvedOgTitle = ogTitle?.trim() || resolvedTitle;
    const resolvedOgDescription = ogDescription?.trim() || resolvedDescription;
    const resolvedCanonicalPath = canonicalPath?.trim() || `${window.location.pathname}${window.location.search}`;
    const resolvedImage = buildAbsoluteUrl(image?.trim() || SEO_DEFAULT_IMAGE, origin);
    const resolvedImageAlt = imageAlt?.trim() || resolvedTitle;
    const resolvedRobots = indexable ? SEO_INDEX_ROBOTS : SEO_NOINDEX_ROBOTS;
    const canonicalUrl = buildAbsoluteUrl(resolvedCanonicalPath, origin);

    ensureManagedTitle().textContent = resolvedTitle;
    document.title = resolvedTitle;

    updateMetaContent('description', 'name', resolvedDescription);
    updateMetaContent('robots', 'name', resolvedRobots);
    updateMetaContent('googlebot', 'name', resolvedRobots);
    updateMetaContent('application-name', 'name', SEO_SITE_NAME);
    updateMetaContent('apple-mobile-web-app-title', 'name', SEO_SITE_NAME);
    updateMetaContent('og:site_name', 'property', SEO_SITE_NAME);
    updateMetaContent('og:title', 'property', resolvedOgTitle);
    updateMetaContent('og:description', 'property', resolvedOgDescription);
    updateMetaContent('og:type', 'property', ogType);
    updateMetaContent('og:url', 'property', canonicalUrl);
    updateMetaContent('og:image', 'property', resolvedImage);
    updateMetaContent('og:image:alt', 'property', resolvedImageAlt);
    updateMetaContent('og:locale', 'property', 'pt_BR');
    updateMetaContent('twitter:card', 'name', 'summary_large_image');
    updateMetaContent('twitter:title', 'name', resolvedOgTitle);
    updateMetaContent('twitter:description', 'name', resolvedOgDescription);
    updateMetaContent('twitter:image', 'name', resolvedImage);
    updateMetaContent('twitter:image:alt', 'name', resolvedImageAlt);

    ensureManagedLink('canonical', 'canonical').setAttribute('href', canonicalUrl);

    const structuredDataElement = ensureManagedScript('structured-data');
    structuredDataElement.textContent = structuredData ? serializeJsonLd(structuredData) : '';
  }, [canonicalPath, description, image, imageAlt, indexable, ogDescription, ogTitle, ogType, structuredData, title]);
}
